import { Injectable, inject } from '@angular/core';
import { BattlecardsService } from '@app/support/services/battlecards.service';
import {
  BattlecardsOperator,
  BattlecardsServiceCategory,
  BattlecardsOffering,
  InternalOfferingsVisibility,
  BattlecardsOfferingRequest,
} from '@app/support/models/battlecards/battlecards.model';
import { Action, State, StateContext } from '@ngxs/store';
import {
  BattlecardsGetOperatorsAction,
  BattlecardsGetOperatorsFailedAction,
  BattlecardsGetOperatorsSuccessAction,
  BattlecardsGetServiceCategoriesAction,
  BattlecardsGetServiceCategoriesFailedAction,
  BattlecardsGetServiceCategoriesSuccessAction,
  BattlecardsGetRecommendedOfferingsSuccessAction,
  BattlecardsGetRecommendedOfferingsFailedAction,
  BattlecardsGetRecommendedOfferingsAction,
  BattlecardsResetRecommendedOfferingsAction,
  BattlecardsSetInternalOfferingsVisibilityAction,
  BattlecardsGetExternalOfferingsAction,
  BattlecardsGetExternalOfferingsSuccessAction,
  BattlecardsGetExternalOfferingsFailedAction,
  BattlecardsAppendInternalOfferingsFailedAction,
  BattlecardsAppendInternalOfferingsSuccessAction,
  BattlecardsAppendInternalOfferingsAction,
  BattlecardsGetInternalOfferingsFailedAction,
  BattlecardsGetInternalOfferingsSuccessAction,
  BattlecardsGetInternalOfferingsAction,
  BattlecardsAppendExternalOfferingsFailedAction,
  BattlecardsAppendExternalOfferingsSuccessAction,
  BattlecardsAppendExternalOfferingsAction,
} from './battlecards.action';
import { catchError, tap } from 'rxjs/operators';
import { PaginationQueryRequest } from '@app/support/models/pagination-query-request.model';

enum Messages {
  GetOperatorsFailed = 'Noe gikk galt under henting av operatører',
  GetOfferingsFailed = 'Noe gikk galt under henting av abonnementer',
}

export interface BattlecardsStateModel {
  operators: BattlecardsOperator[];
  isGettingOperators: boolean;
  externalOfferings: BattlecardsOffering[];
  isGettingExternalOfferings: boolean;
  internalOfferings: BattlecardsOffering[];
  isGettingInternalOfferings: boolean;
  totalExternalOfferings: number;
  totalInternalOfferings: number;
  previousExternalSearch: BattlecardsOfferingRequest;
  previousInternalSearch: BattlecardsOfferingRequest;
  isAppendingMoreExternalOfferings: boolean;
  isAppendingMoreInternalOfferings: boolean;
  serviceCategories: BattlecardsServiceCategory[];
  isGettingServiceCategories: boolean;
  isGettingRecommendedOfferings: boolean;
  internalOfferingsVisibility: InternalOfferingsVisibility;
}

@State<BattlecardsStateModel>({
  name: 'battlecards',
  defaults: {
    operators: [],
    isGettingOperators: false,
    externalOfferings: [],
    isGettingExternalOfferings: false,
    internalOfferings: [],
    isGettingInternalOfferings: false,
    totalExternalOfferings: 0,
    totalInternalOfferings: 0,
    previousExternalSearch: null,
    previousInternalSearch: null,
    isAppendingMoreExternalOfferings: false,
    isAppendingMoreInternalOfferings: false,
    serviceCategories: [],
    isGettingServiceCategories: false,
    isGettingRecommendedOfferings: false,
    internalOfferingsVisibility: InternalOfferingsVisibility.Hide,
  },
})
@Injectable()
export class BattlecardsState {
  private battlecardsService = inject(BattlecardsService);

  @Action(BattlecardsGetOperatorsAction)
  public battlecardsGetOperators({ patchState, dispatch }: StateContext<BattlecardsStateModel>) {
    patchState({ isGettingOperators: true });
    return this.battlecardsService.getOperators().pipe(
      tap(response => dispatch(new BattlecardsGetOperatorsSuccessAction(response))),
      catchError(() => dispatch(new BattlecardsGetOperatorsFailedAction(Messages.GetOperatorsFailed)))
    );
  }

  @Action(BattlecardsGetOperatorsSuccessAction)
  public battlecardsGetOperatorsSuccess(
    { patchState }: StateContext<BattlecardsStateModel>,
    { operators }: BattlecardsGetOperatorsSuccessAction
  ) {
    patchState({ operators, isGettingOperators: false });
  }

  @Action(BattlecardsGetOperatorsFailedAction)
  public battlecardsGetOperatorsFailed({ patchState }: StateContext<BattlecardsStateModel>) {
    patchState({ isGettingOperators: false });
  }

  @Action(BattlecardsGetExternalOfferingsAction)
  public battlecardsGetExternalOfferings(
    { patchState, dispatch }: StateContext<BattlecardsStateModel>,
    request: BattlecardsGetExternalOfferingsAction
  ) {
    patchState({ isGettingExternalOfferings: true });
    return this.battlecardsService.getExternalOfferings(request.params).pipe(
      tap(response => dispatch(new BattlecardsGetExternalOfferingsSuccessAction(response))),
      catchError(() => dispatch(new BattlecardsGetExternalOfferingsFailedAction(Messages.GetOfferingsFailed)))
    );
  }

  @Action(BattlecardsGetExternalOfferingsSuccessAction)
  public battlecardsGetExternalOfferingsSuccess(
    { patchState }: StateContext<BattlecardsStateModel>,
    { response }: BattlecardsGetExternalOfferingsSuccessAction
  ) {
    patchState({
      previousExternalSearch: response,
      externalOfferings: response.data,
      totalExternalOfferings: response.totalRows,
      isGettingExternalOfferings: false,
    });
  }

  @Action(BattlecardsGetExternalOfferingsFailedAction)
  public battlecardsGetExternalOfferingsFailed({ patchState }: StateContext<BattlecardsStateModel>) {
    patchState({ isGettingExternalOfferings: false });
  }

  @Action(BattlecardsAppendExternalOfferingsAction)
  public battlecardsAppendExternalOfferings({ patchState, getState, dispatch }: StateContext<BattlecardsStateModel>) {
    const state = getState();
    const previousSearch = state.previousExternalSearch;

    const newSearch: BattlecardsOfferingRequest = {
      ...previousSearch,
      currentPage: previousSearch.currentPage + 1,
      ageCategories: previousSearch.ageCategories,
      operators: previousSearch.operators,
      serviceCategories: previousSearch.serviceCategories,
    };

    patchState({ isAppendingMoreExternalOfferings: true });

    return this.battlecardsService.getExternalOfferings(newSearch).pipe(
      tap(response => dispatch(new BattlecardsAppendExternalOfferingsSuccessAction(response, newSearch))),
      catchError(() => dispatch(new BattlecardsAppendExternalOfferingsFailedAction()))
    );
  }

  @Action(BattlecardsAppendExternalOfferingsSuccessAction)
  public battlecardsAppendExternalOfferingsSuccess(
    { patchState, getState }: StateContext<BattlecardsStateModel>,
    { response }: BattlecardsAppendExternalOfferingsSuccessAction
  ) {
    const state = getState();
    patchState({
      externalOfferings: [...state.externalOfferings, ...response.data],
      totalExternalOfferings: response.totalRows,
      isAppendingMoreExternalOfferings: false,
      previousExternalSearch: response,
    });
  }

  @Action(BattlecardsAppendExternalOfferingsFailedAction)
  public battlecardsAppendExternalOfferingsFailed({ patchState }: StateContext<BattlecardsStateModel>) {
    patchState({ isAppendingMoreExternalOfferings: false });
  }

  @Action(BattlecardsGetInternalOfferingsAction)
  public battlecardsGetInternalOfferings(
    { patchState, dispatch }: StateContext<BattlecardsStateModel>,
    request: BattlecardsGetInternalOfferingsAction
  ) {
    patchState({ isGettingInternalOfferings: true });
    return this.battlecardsService.getInternalOfferings(request.params).pipe(
      tap(response => dispatch(new BattlecardsGetInternalOfferingsSuccessAction(response))),
      catchError(() => dispatch(new BattlecardsGetInternalOfferingsFailedAction(Messages.GetOfferingsFailed)))
    );
  }

  @Action(BattlecardsGetInternalOfferingsSuccessAction)
  public battlecardsGetInternalOfferingsSuccess(
    { patchState }: StateContext<BattlecardsStateModel>,
    { response }: BattlecardsGetInternalOfferingsSuccessAction
  ) {
    patchState({
      internalOfferings: response.data,
      totalInternalOfferings: response.totalRows,
      isGettingInternalOfferings: false,
    });
  }

  @Action(BattlecardsGetInternalOfferingsFailedAction)
  public battlecardsGetInternalOfferingsFailed({ patchState }: StateContext<BattlecardsStateModel>) {
    patchState({ isGettingInternalOfferings: false });
  }

  @Action(BattlecardsAppendInternalOfferingsAction)
  public battlecardsAppendInternalOfferings({ patchState, getState, dispatch }: StateContext<BattlecardsStateModel>) {
    const state = getState();
    const previousSearch = state.previousInternalSearch;

    const newSearch: PaginationQueryRequest = {
      ...previousSearch,
      currentPage: previousSearch.currentPage + 1,
    };

    patchState({ isAppendingMoreInternalOfferings: true });

    return this.battlecardsService.getExternalOfferings(newSearch).pipe(
      tap(response => dispatch(new BattlecardsAppendInternalOfferingsSuccessAction(response, newSearch))),
      catchError(() => dispatch(new BattlecardsAppendInternalOfferingsFailedAction(Messages.GetOfferingsFailed)))
    );
  }

  @Action(BattlecardsAppendInternalOfferingsSuccessAction)
  public battlecardsAppendInternalOfferingsSuccess(
    { patchState, getState }: StateContext<BattlecardsStateModel>,
    { response }: BattlecardsAppendInternalOfferingsSuccessAction
  ) {
    const state = getState();
    patchState({
      internalOfferings: [...state.internalOfferings, ...response.data],
      isAppendingMoreInternalOfferings: false,
      previousInternalSearch: response,
    });
  }

  @Action(BattlecardsAppendInternalOfferingsFailedAction)
  public battlecardsAppendInternalOfferingsFailed({ patchState }: StateContext<BattlecardsStateModel>) {
    patchState({ isAppendingMoreInternalOfferings: false });
  }

  @Action(BattlecardsGetServiceCategoriesAction)
  public battlecardsGetServiceCategories({ dispatch, patchState }: StateContext<BattlecardsStateModel>) {
    patchState({ isGettingServiceCategories: true });
    return this.battlecardsService.getServiceCategories().pipe(
      tap(response => dispatch(new BattlecardsGetServiceCategoriesSuccessAction(response))),
      catchError(() => dispatch(new BattlecardsGetServiceCategoriesFailedAction()))
    );
  }

  @Action(BattlecardsGetServiceCategoriesSuccessAction)
  public battlecardsGetServiceCategoriesSuccess(
    { patchState }: StateContext<BattlecardsStateModel>,
    { serviceCategories }: BattlecardsGetServiceCategoriesSuccessAction
  ) {
    patchState({ serviceCategories, isGettingServiceCategories: false });
  }

  @Action(BattlecardsGetServiceCategoriesFailedAction)
  public battlecardsGetServiceCategoriesFailed({ patchState }: StateContext<BattlecardsStateModel>) {
    patchState({ serviceCategories: [], isGettingServiceCategories: false });
  }

  @Action(BattlecardsGetRecommendedOfferingsAction)
  public battlecardsGetRecommendedOfferings(
    { dispatch, patchState }: StateContext<BattlecardsStateModel>,
    { offeringId }: BattlecardsGetRecommendedOfferingsAction
  ) {
    patchState({ isGettingRecommendedOfferings: true });
    return this.battlecardsService.getRecommendedOfferings(offeringId).pipe(
      tap(response => dispatch(new BattlecardsGetRecommendedOfferingsSuccessAction(response))),
      catchError(() => dispatch(new BattlecardsGetRecommendedOfferingsFailedAction()))
    );
  }

  @Action(BattlecardsGetRecommendedOfferingsSuccessAction)
  public battlecardsGetRecommendedOfferingsSuccess(
    { patchState, getState }: StateContext<BattlecardsStateModel>,
    { recommendedOfferings }: BattlecardsGetRecommendedOfferingsSuccessAction
  ) {
    const state = getState();

    const newInternalOfferings = state.internalOfferings.map(internalOffering => {
      const recommendation = recommendedOfferings.find(offering => offering.sanityId === internalOffering.id);

      return { ...internalOffering, recommendationBasis: recommendation?.recommendationBackground ?? null };
    });

    const internalOfferingsSorted = this.sortInternalOfferings(newInternalOfferings);

    patchState({
      internalOfferings: internalOfferingsSorted,
      isGettingRecommendedOfferings: false,
      internalOfferingsVisibility: InternalOfferingsVisibility.OnlyShowRecommended,
    });
  }

  @Action(BattlecardsGetRecommendedOfferingsFailedAction)
  public battlecardsGetRecommendedOfferingsFailed({ patchState }: StateContext<BattlecardsStateModel>) {
    patchState({ isGettingRecommendedOfferings: false });
  }

  @Action(BattlecardsResetRecommendedOfferingsAction)
  public battlecardsResetRecommendedOfferings({ patchState, getState }: StateContext<BattlecardsStateModel>) {
    const state = getState();
    const internalOfferings = state.internalOfferings.map(offering => {
      return { ...offering, recommendationBasis: null };
    });
    if (state.internalOfferingsVisibility === InternalOfferingsVisibility.OnlyShowRecommended) {
      patchState({ internalOfferings, internalOfferingsVisibility: InternalOfferingsVisibility.Hide });
    } else {
      patchState({
        internalOfferings: internalOfferings,
      });
    }
  }

  @Action(BattlecardsSetInternalOfferingsVisibilityAction)
  public battlecardsSetInternalOfferingsVisibility(
    { patchState }: StateContext<BattlecardsStateModel>,
    { visibility }: BattlecardsSetInternalOfferingsVisibilityAction
  ) {
    patchState({ internalOfferingsVisibility: visibility });
  }

  /**
   * Sorts internal offerings based on specified criteria.
   * The criteria include:
   * 1. Whether an offering has a recommended background.
   * 2. Monthly price in descending order.
   *
   * @param internalOfferings - The array of internal offerings to be sorted.
   * @returns A new array of internal offerings sorted based on the criteria.
   */
  private sortInternalOfferings(internalOfferings: BattlecardsOffering[]): BattlecardsOffering[] {
    return internalOfferings.sort((a, b) => {
      const aHasRecommendation = a.recommendationBasis ? 1 : 0;
      const bHasRecommendation = b.recommendationBasis ? 1 : 0;

      if (aHasRecommendation !== bHasRecommendation) {
        return bHasRecommendation - aHasRecommendation;
      }

      return b.monthlyPrice - a.monthlyPrice;
    });
  }
}
